import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PlayCircle } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { PageAccent } from "../components/PageAccent";
import { FadeUp } from "../components/Reveal";
import { IMAGES } from "../data/content";
import { EmSwash } from "../components/EmSwash";
import { useNoIndex } from "../lib/useNoIndex";

/**
 * Preview — split-screen gateway into the mocked prototype.
 *
 * Two clear doors on the way to /demo:
 *   Left  — Preview donor experience  → /demo (browse campaigns, chip in)
 *   Right — Preview student experience → /demo?flow=student
 *             (auto-opens the Create Campaign wizard)
 *
 * Guest checkout is enabled in the prototype, so donors never need to
 * sign in just to browse or give. Sign-in is only for returning donors
 * managing a recurring gift.
 */
const PATHS = [
  {
    key: "donor",
    title: "Preview the donor experience.",
    body: "Browse student campaigns, follow a story, and try the giving flow exactly as donors will on launch day.",
    photo: {
      src: IMAGES.studentSteps,
      alt: "A student sitting on stone steps — the kind of face donors meet on a campaign page",
    },
    cta: { label: "Preview donor experience", target: "/demo" },
    reel: { label: "Watch the 60-second story", target: "/demo/donor-story" },
    testid: "preview-donor",
    borderClass: "border-tangerine",
    tintClass: "bg-tangerine/[0.04]",
  },
  {
    key: "student",
    title: "Preview the student experience.",
    body: "Step through the same builder students will use to share their stories and launch their campaigns.",
    photo: {
      src: IMAGES.classroomHandUp,
      alt: "A student raising her hand in a diverse classroom — the students building their own campaign pages",
    },
    cta: { label: "Preview student experience", target: "/demo?flow=student" },
    reel: { label: "Watch the 60-second story", target: "/demo/story" },
    testid: "preview-student",
    borderClass: "border-cobalt",
    tintClass: "bg-cobalt/[0.04]",
  },
];

export default function Preview() {
  useNoIndex();
  const navigate = useNavigate();
  const openDemoDonor = () => navigate("/demo");
  const openDemoStudent = () => navigate("/demo?flow=student");

  return (
    <main data-testid="preview-page" className="bg-sand">
      <Navbar onStart={openDemoDonor} primaryLabel="Preview the platform" />

      <section className="mx-auto max-w-7xl px-6 pb-16 pt-32 lg:px-8 lg:pt-44">
        <FadeUp tier="sm">
          <PageAccent className="mb-5" />
        </FadeUp>
        <FadeUp>
          <h1 className="max-w-3xl font-serif text-[3.5rem] lg:text-[5.5rem] leading-[0.95] tracking-tight text-ink">
            Preview the Donor and Student <EmSwash>Experience</EmSwash>
          </h1>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-8 lg:pb-36">
        <div className="grid grid-cols-1 gap-14 md:grid-cols-2 md:gap-10 lg:gap-14">
          {PATHS.map((p, i) => (
              <FadeUp key={p.key} delay={i * 0.08}>
                <motion.article
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
                  className={`flex h-full flex-col rounded-2xl border-2 ${p.borderClass} ${p.tintClass} p-6 lg:p-8`}
                  data-testid={`${p.testid}-card`}
                >
                  <div className="w-full max-w-[440px]">
                      <div className="relative aspect-[4/5] overflow-hidden rounded-2xl">
                        <img
                          src={p.photo.src}
                          alt={p.photo.alt}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      </div>
                  </div>

                  <div className="mt-8 flex flex-1 flex-col">
                    <h2 className="font-serif text-3xl leading-tight text-ink">
                      {p.title}
                    </h2>
                    <p className="mt-5 max-w-md text-base leading-relaxed text-olive">
                      {p.body}
                    </p>

                    <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4">
                      <button
                        type="button"
                        onClick={() => navigate(p.cta.target)}
                        data-testid={`${p.testid}-button`}
                        className="inline-flex w-fit items-center gap-2 rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest"
                      >
                        {p.cta.label} <ArrowRight className="h-4 w-4" strokeWidth={2} />
                      </button>
                      <Link
                        to={p.reel.target}
                        data-testid={`${p.testid}-reel-link`}
                        className="inline-flex items-center gap-2 text-base font-semibold text-ink underline decoration-tangerine decoration-2 underline-offset-4 transition-colors hover:text-tangerine"
                      >
                        <PlayCircle className="h-4 w-4" strokeWidth={2} />
                        {p.reel.label}
                      </Link>
                    </div>
                  </div>
                </motion.article>
              </FadeUp>
          ))}
        </div>
      </section>

      <Footer
        onStart={openDemoStudent}
        onDonate={openDemoDonor}
        primaryLabel="Preview donor experience"
        secondaryLabel="Preview student experience"
      />
    </main>
  );
}
